import axios from '@/libs/axios'

export const defaultFilters = () => ({
    merchant_sku: '',
    product_name: '',
    brand_id: '',
    category_id: '',
    has_category: '',
    matched: '',
    in_store: '',
    store_id: '',
    missing_required: '',
    missing_required_attribute_id: '',
    has_batch_error: '',
    batch_store_id: '',
})

function apiFilters(filters) {
    const { brand_id: brandId, ...rest } = filters
    const params = {}
    Object.keys(rest).forEach(key => {
        if (rest[key] !== '') params[key] = rest[key]
    })
    if (brandId) params.brand_ids = [Number(brandId)]
    return params
}

const emptySelection = () => ({
    allSelected: false,
    selectedIds: [],
    excludedIds: [],
    selectionTotal: 0,
})

export const classificationLabels = {
    missing_category: 'Kategori yok',
    missing_metadata: 'Özellik tanımları eksik',
    incomplete: 'Zorunlu özellikler eksik',
    complete: 'Zorunlu özellikler tamam',
}

export function errorMessage(error) {
    if (error.response && error.response.status === 403) return 'Bu işlem için yetkiniz yok.'
    const body = error.response && error.response.data
    return (body && body.message) || error.message || 'İşlem tamamlanamadı. Lütfen tekrar deneyin.'
}

async function content(request) {
    const { data } = await request
    if (data.status !== 'success') throw new Error(data.message || 'İşlem tamamlanamadı.')
    return data.content
}

export default {
    namespaced: true,
    state: () => ({
        filters: defaultFilters(),
        products: [],
        pagination: null,
        perPage: 25,
        listReady: false,
        selectedProduct: null,
        listVersion: 0,
        requiredAttributes: [],
        bulkValues: [],
        assigning: false,
        ...emptySelection(),
    }),
    getters: {
        isProductSelected: state => id => (state.allSelected ? !state.excludedIds.includes(id) : state.selectedIds.includes(id)),
        selectedCount: state => (state.allSelected ? Math.max(0, state.selectionTotal - state.excludedIds.length) : state.selectedIds.length),
    },
    mutations: {
        clearSelection(state) { Object.assign(state, emptySelection()) },
        setAssigning(state, assigning) { state.assigning = assigning },
        setProductSelection(state, { ids, selected }) {
            const key = state.allSelected ? 'excludedIds' : 'selectedIds'
            const values = new Set(state[key])
            ids.forEach(id => {
                if (state.allSelected ? !selected : selected) values.add(id)
                else values.delete(id)
            })
            state[key] = [...values]
        },
        selectAllProducts(state) {
            Object.assign(state, emptySelection(), {
                allSelected: true,
                selectionTotal: state.pagination ? state.pagination.total : 0,
            })
        },
        setFilters(state, filters) {
            if (state.filters.missing_required_attribute_id !== filters.missing_required_attribute_id) state.bulkValues = []
            Object.assign(state, emptySelection())
            state.filters = { ...filters }
        },
        setBulkValues(state, values) { state.bulkValues = values },
        setRequiredAttributes(state, attributes) { state.requiredAttributes = attributes },
        setPerPage(state, perPage) { state.perPage = Number(perPage) },
        invalidateList(state) {
            state.listVersion += 1
            state.listReady = false
        },
        setProducts(state, result) {
            const { data, ...pagination } = result
            state.products = data
            state.pagination = pagination
            state.listReady = true
        },
        setSelectedProduct(state, product) { state.selectedProduct = product },
        updateProduct(state, product) {
            state.selectedProduct = product
            const index = state.products.findIndex(item => item.id === product.id)
            if (index !== -1) state.products.splice(index, 1, product)
        },
    },
    actions: {
        async loadRequiredAttributes({ commit }) {
            const attributes = await content(axios.get('/v2/trendyol/required-attributes'))
            commit('setRequiredAttributes', attributes)
            return attributes
        },
        async loadProducts({ state, commit }, page = 1) {
            commit('invalidateList')
            const version = state.listVersion
            const params = { ...apiFilters(state.filters), page, per_page: state.perPage }
            const result = await content(axios.get('/v2/trendyol/products', { params }))
            if (version === state.listVersion) commit('setProducts', result)
            return result
        },
        async loadBulkValues({ state, commit }, attributeId) {
            const values = await content(axios.get(`/v2/trendyol/required-attributes/${attributeId}/values`))
            if (state.filters.missing_required_attribute_id === attributeId) commit('setBulkValues', values)
        },
        async bulkAssign({ state, getters, commit }, { filters, valueName }) {
            if (!state.listReady || state.assigning) throw new Error('Ürün listesinin yüklenmesini bekleyin.')
            if (!getters.selectedCount) throw new Error('Atama için ürün seçin.')
            const selection = state.allSelected ? {
                selection_mode: 'all',
                excluded_product_ids: [...state.excludedIds],
            } : {
                selection_mode: 'explicit',
                product_ids: [...state.selectedIds],
            }
            commit('setAssigning', true)
            try {
                const result = await content(axios.post('/v2/trendyol/products/bulk-assign-attribute', {
                    ...apiFilters(filters),
                    value_name: valueName,
                    ...selection,
                }))
                commit('clearSelection')
                return result
            } finally {
                commit('setAssigning', false)
            }
        },
        loadProduct(context, id) {
            return content(axios.get(`/v2/trendyol/products/${id}`))
        },
        async saveProduct({ commit }, { id, payload }) {
            const product = await content(axios.put(`/v2/trendyol/products/${id}/classification`, payload))
            commit('updateProduct', product)
            return product
        },
        loadCategories(context, params) {
            return content(axios.get('/v2/trendyol/categories', { params }))
        },
        loadAttributes(context, categoryId) {
            return content(axios.get(`/v2/trendyol/categories/${categoryId}/attributes`))
        },
        loadValues(context, { categoryId, attributeId, params }) {
            return content(axios.get(`/v2/trendyol/categories/${categoryId}/attributes/${attributeId}/values`, { params }))
        },
    },
}
